import { useState, useMemo, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { registerSchema } from '@flymind/shared';
import { useAuth } from '../hooks/useAuth';
import { useSocialAuth } from '../hooks/useSocialAuth';
import { useTranslation } from '../hooks/useTranslation';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';

function PasswordStrength({ password }: { password: string }) {
  const { t } = useTranslation();
  const checks = useMemo(() => {
    return {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      number: /[0-9]/.test(password),
    };
  }, [password]);

  const strength = Object.values(checks).filter(Boolean).length;

  if (password.length === 0) return null;

  const strengthColor =
    strength <= 1
      ? 'bg-red-500'
      : strength === 2
        ? 'bg-yellow-500'
        : 'bg-green-500';

  const strengthLabel =
    strength <= 1
      ? t('auth.pwStrengthWeak')
      : strength === 2
        ? t('auth.pwStrengthMedium')
        : t('auth.pwStrengthStrong');

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <div className="flex flex-1 gap-1">
          {[1, 2, 3].map((level) => (
            <div
              key={level}
              className={`h-1.5 flex-1 rounded-full transition-colors ${
                level <= strength ? strengthColor : 'bg-border'
              }`}
            />
          ))}
        </div>
        <span className="text-xs text-text-secondary">{strengthLabel}</span>
      </div>
      <ul className="flex flex-col gap-0.5">
        {[
          { key: 'length', label: t('auth.pwReqLength') },
          { key: 'uppercase', label: t('auth.pwReqUppercase') },
          { key: 'number', label: t('auth.pwReqNumber') },
        ].map(({ key, label }) => (
          <li
            key={key}
            className={`flex items-center gap-1.5 text-xs ${
              checks[key as keyof typeof checks]
                ? 'text-green-600'
                : 'text-text-secondary'
            }`}
          >
            {checks[key as keyof typeof checks] ? (
              <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            ) : (
              <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                  clipRule="evenodd"
                />
              </svg>
            )}
            {label}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Register() {
  const { register } = useAuth();
  const { signInWithGoogle, signInWithApple } = useSocialAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get('ref') || undefined;

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState(false);

  // OAuth is handled entirely by /sso-callback page

  const handleProviderLogin = async (provider: 'google' | 'apple') => {
    setSocialLoading(true);
    setApiError('');
    try {
      if (provider === 'google') await signInWithGoogle();
      else await signInWithApple();
    } catch (err) {
      setApiError(err instanceof Error ? err.message : t('auth.socialLoginError'));
      setSocialLoading(false);
    }
  };

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setErrors({});
    setApiError('');

    // Validate with Zod schema
    const result = registerSchema.safeParse({
      email,
      password,
      name: name.trim(),
    });

    const fieldErrors: Record<string, string> = {};

    if (!result.success) {
      for (const issue of result.error.issues) {
        const field = issue.path[0] as string;
        if (!fieldErrors[field]) {
          fieldErrors[field] = issue.message;
        }
      }
    }

    // Confirm password check
    if (password !== confirmPassword) {
      fieldErrors.confirmPassword = t('auth.passwordsDontMatch');
    }

    // Terms check
    if (!termsAccepted) {
      fieldErrors.terms = t('auth.mustAcceptTerms');
    }

    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      return;
    }

    setIsLoading(true);
    try {
      await register({
        email: result.success ? result.data.email : email.toLowerCase().trim(),
        password,
        name: name.trim(),
        ...(referralCode ? { referralCode } : {}),
      });
      navigate('/onboarding');
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : t('errors.generic');
      setApiError(message);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div>
      {referralCode && (
        <div className="mb-4 flex items-center justify-center gap-2 rounded-lg bg-primary-50 border border-primary-200 px-4 py-2.5">
          <svg className="h-4 w-4 text-primary-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 11.25v8.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 109.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1114.625 7.5H12m0 0V21m-8.25-9.75h16.5" />
          </svg>
          <span className="text-sm font-medium text-primary-700">
            {t('auth.invitedByFriend')}
          </span>
        </div>
      )}

      <div className="mb-6 text-center">
        <h2 className="text-xl font-bold text-text-primary">
          {t('auth.registerTitle')}
        </h2>
        <p className="mt-1 text-sm text-text-secondary">
          {t('auth.registerSubtitle')}
        </p>
      </div>

      {apiError && (
        <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {apiError}
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <Input
          label={t('auth.nameLabel')}
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('auth.namePlaceholder')}
          error={errors.name}
          autoComplete="name"
          required
        />

        <Input
          label={t('auth.emailLabel')}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('auth.emailPlaceholder')}
          error={errors.email}
          required
          autoComplete="email"
        />

        <div className="flex flex-col gap-2">
          <Input
            label={t('auth.passwordLabel')}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('auth.minPasswordPlaceholder')}
            error={errors.password}
            required
            autoComplete="new-password"
          />
          <PasswordStrength password={password} />
        </div>

        <Input
          label={t('auth.confirmPassword')}
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder={t('auth.repeatPasswordPlaceholder')}
          error={errors.confirmPassword}
          required
          autoComplete="new-password"
        />

        <div className="flex flex-col gap-1">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-border text-primary-600 focus:ring-primary-500"
            />
            <span className="text-xs text-text-secondary leading-relaxed">
              {t('auth.termsAgreement')}
            </span>
          </label>
          {errors.terms && (
            <p className="text-xs text-alert-exceptional">{errors.terms}</p>
          )}
        </div>

        <Button type="submit" size="lg" isLoading={isLoading} className="w-full">
          {t('auth.register')}
        </Button>
      </form>

      <div className="my-6 flex items-center gap-3">
        <div className="h-px flex-1 bg-border" />
        <span className="text-xs text-text-secondary uppercase">
          {t('common.or')}
        </span>
        <div className="h-px flex-1 bg-border" />
      </div>

      <div className="flex flex-col gap-2">
        <button type="button" onClick={() => handleProviderLogin('google')} disabled={socialLoading}
          className="w-full flex items-center justify-center gap-3 rounded-lg border border-border bg-white px-4 py-2.5 text-sm font-medium text-text-primary hover:bg-gray-50 transition-colors disabled:opacity-50 cursor-pointer">
          <svg className="h-5 w-5 shrink-0" viewBox="0 0 24 24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          {t('auth.continueWithGoogle')}
        </button>
        <button type="button" onClick={() => handleProviderLogin('apple')} disabled={socialLoading}
          className="w-full flex items-center justify-center gap-3 rounded-lg border border-border bg-black px-4 py-2.5 text-sm font-medium text-white hover:bg-gray-900 transition-colors disabled:opacity-50 cursor-pointer">
          <svg className="h-5 w-5 shrink-0" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
          </svg>
          {t('auth.continueWithApple')}
        </button>
        <button type="button" disabled
          className="w-full flex items-center justify-center gap-3 rounded-lg border border-border bg-gray-100 px-4 py-2.5 text-sm font-medium text-gray-400 cursor-not-allowed opacity-60">
          <svg className="h-5 w-5 shrink-0" viewBox="0 0 24 24" fill="currentColor">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
          </svg>
          {t('auth.facebookComingSoon')}
        </button>
      </div>

      <p className="mt-6 text-center text-sm text-text-secondary">
        {t('auth.hasAccount')}{' '}
        <Link
          to="/login"
          className="font-medium text-primary-600 hover:text-primary-700 hover:underline"
        >
          {t('auth.login')}
        </Link>
      </p>
    </div>
  );
}
